import { useState, useEffect, useCallback } from 'react';
import { apiRequest } from '../../services/api';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import ActionButtons, { EditButton } from '../../components/ActionButtons';
import OnayPopup from '../../components/OnayPopup';
import UsageInfo from '../../components/UsageInfo';
import ModalShell from '../../components/ModalShell';
import CustomSelect from '../../components/CustomSelect';
import Buton from '../../components/Buton';
import { vergiAyristir } from '../../utils/vergi';
import { useKasa } from '../../context/KasaContext';

const INP = 'w-full px-5 py-3.5 rounded-2xl border-2 border-gray-100 text-xs font-bold text-gray-800 focus:border-violet-400 focus:ring-4 focus:ring-violet-100 outline-none transition-all';
const LBL = 'block text-[10px] font-black uppercase tracking-widest text-gray-400 mb-2.5 ml-1 leading-none';

const BOS_TIP = { ad: '', aciklama: '', ikon: '🧖', kdvOrani: null, kapasite: 1, varsayilanSureDk: 60, acilisSaati: '09:00', kapanisSaati: '22:00', siraNo: 0, aktifMi: true };

// Yeni hizmet eklerken listeden seçilen simgeler
const IKONLAR = ['🧖', '🔥', '🧂', '🚿', '🏋️', '💆', '🏊', '🧴', '💅', '✂️', '🕯️', '🍵'];
const BOS_PAKET = { ad: '', aciklama: '', sureDk: 0, disFiyat: 0, misafirFiyat: null, paraBirimi: 'TL', siraNo: 0, aktifMi: true };

function para(n, pb = 'TL') {
  return Number(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ' + pb;
}

/**
 * Fiyatın içindeki KDV tutarı — hizmet fiyatları vergi dahil girilir.
 * Hesap tek kaynaktan: utils/vergi.js (backend karşılığı VergiHesaplayici).
 * Oran tanımlı değilse rapor otel geneli oranı uygular, burada gösterilmez.
 */
function kdvNotu(fiyat, oran, pb) {
  if (oran == null || !fiyat) return null;
  return `KDV %${oran} · ${para(vergiAyristir(fiyat, oran, 0).kdv, pb)}`;
}

export default function EkHizmetler() {
  // Para birimleri ve kurlar tanımlardan gelir; sabit liste tutulmaz.
  const { paraBirimleri, dovizKurlari } = useKasa();

  const paraBirimiSecenekleri = [
    { deger: 'TL', etiket: 'TL' },
    ...(paraBirimleri || [])
      .filter(p => p.tcmbKod !== 'TL' && p.tcmbKod !== 'TRY')
      .map(p => ({ deger: p.tcmbKod, etiket: p.tcmbKod })),
  ];

  const tlKarsiligi = (tutar, para) => {
    if (!tutar || para === 'TL' || para === 'TRY') return null;
    const kur = dovizKurlari?.[para];
    if (!kur) return null;
    return parseFloat(tutar) * kur;
  };

  const [tipler, setTipler] = useState([]);
  const [secili, setSecili] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(true);
  const [hata, setHata] = useState('');
  const [mesaj, setMesaj] = useState('');

  const [tipForm, setTipForm] = useState(null);       // null = kapalı
  const [paketForm, setPaketForm] = useState(null);
  const [silOnay, setSilOnay] = useState(null);       // { tur: 'tip'|'paket', id }

  const getir = useCallback(async () => {
    setYukleniyor(true); setHata('');
    try {
      const d = await apiRequest('hizmet/tipler');
      setTipler(d || []);
      setSecili(p => (p ? (d || []).find(t => t.hizmetTipiId === p.hizmetTipiId) || null : (d || [])[0] || null));
    } catch (e) { setHata(e?.message || 'Hizmetler alınamadı.'); }
    finally { setYukleniyor(false); }
  }, []);

  useEffect(() => { getir(); }, [getir]);

  const bildir = (m) => { setMesaj(m); setTimeout(() => setMesaj(''), 3500); };

  const tipKaydet = async () => {
    try {
      if (tipForm.hizmetTipiId) await apiRequest('hizmet/tipler', 'PUT', tipForm);
      else await apiRequest('hizmet/tipler', 'POST', tipForm);
      setTipForm(null); bildir('Hizmet kaydedildi.'); getir();
    } catch (e) { setHata(e?.message || 'Kaydedilemedi.'); }
  };

  const paketKaydet = async () => {
    try {
      const govde = { ...paketForm, hizmetTipiId: secili.hizmetTipiId };
      if (govde.hizmetPaketiId) await apiRequest('hizmet/paketler', 'PUT', govde);
      else await apiRequest('hizmet/paketler', 'POST', govde);
      setPaketForm(null); bildir('Paket kaydedildi.'); getir();
    } catch (e) { setHata(e?.message || 'Kaydedilemedi.'); }
  };

  const sil = async () => {
    try {
      if (silOnay.tur === 'tip') await apiRequest(`hizmet/tipler/${silOnay.id}`, 'DELETE');
      else await apiRequest(`hizmet/paketler/${silOnay.id}`, 'DELETE');
      setSilOnay(null); bildir('Silindi.'); getir();
    } catch (e) { setSilOnay(null); setHata(e?.message || 'Silinemedi.'); }
  };

  if (yukleniyor) return <LoadingSpinner fullPage text="Hizmetler yükleniyor…" />;

  return (
    <div className="p-4 md:p-6 space-y-6">
      <PageHeader
        icon="🧖"
        iconColor="from-violet-600 to-fuchsia-700"
        iconShadow="shadow-violet-200"
        title="Ek Hizmetler"
        subtitle="Hamam, sauna, masaj gibi randevulu hizmetler ve paket fiyatları"
      >
        <Buton tur="ikincil" onClick={() => setTipForm({ ...BOS_TIP })}>
          + Listede Olmayan Hizmet
        </Buton>
      </PageHeader>

      <div className="rounded-2xl bg-sky-50 border border-sky-200 px-5 py-4 text-sm text-sky-800">
        Hamam, sauna, tuz odası, şok duş, fitness ve masaj <strong>Ayarlar → Modüller</strong> sayfasından
        açılır; açtığınız hizmet burada hazır gelir, siz yalnızca paket ve fiyatlarını girersiniz.
        Yukarıdaki buton, o listede bulunmayan kendi hizmetiniz için.
      </div>

      {hata && <div className="rounded-2xl bg-rose-50 border border-rose-200 text-rose-700 px-5 py-4 text-sm font-semibold">{hata}</div>}
      {mesaj && <div className="rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-700 px-5 py-4 text-sm font-semibold">{mesaj}</div>}

      {tipler.length === 0 ? (
        <EmptyState icon="🧖" text="Henüz hizmet açılmamış. Ayarlar → Modüller sayfasından kullandığınız hizmetleri açın." />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Hizmet listesi */}
          <div className="space-y-3">
            {tipler.map(t => (
              <button key={t.hizmetTipiId} onClick={() => setSecili(t)}
                className={`w-full text-left bg-white rounded-[1.5rem] border p-4 transition ${
                  secili?.hizmetTipiId === t.hizmetTipiId ? 'border-violet-400 ring-2 ring-violet-100' : 'border-slate-100 hover:border-slate-200'}`}>
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-emoji">{t.ikon || '🧖'}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-slate-700 truncate">{t.ad}</p>
                    <p className="text-[11px] text-slate-400">
                      {t.paketler?.length || 0} paket · kapasite {t.kapasite} · {t.acilisSaati}-{t.kapanisSaati}
                    </p>
                  </div>
                  <span className={`w-2.5 h-2.5 rounded-full ${t.aktifMi ? 'bg-emerald-400' : 'bg-slate-300'}`} />
                </div>
              </button>
            ))}
          </div>

          {/* Detay */}
          <div className="lg:col-span-2 space-y-4">
            {secili && (
              <>
                <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm p-6">
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <h3 className="text-xl font-black text-slate-800 flex items-center gap-2">
                        <span className="font-emoji">{secili.ikon || '🧖'}</span> {secili.ad}
                      </h3>
                      {secili.aciklama && <p className="text-sm text-slate-500 mt-1">{secili.aciklama}</p>}
                      {secili.anahtar && (
                        <p className="text-[11px] font-bold text-slate-400 mt-2">
                          Bu hizmet Ayarlar → Modüller sayfasından açılmıştır; kaldırmak için oradan kapatın.
                        </p>
                      )}
                    </div>
                    {/* Modülden gelen hizmet silinmez, yalnızca düzenlenir */}
                    {secili.anahtar
                      ? <EditButton onClick={() => setTipForm({ ...secili })} />
                      : <ActionButtons
                          onEdit={() => setTipForm({ ...secili })}
                          onDelete={() => setSilOnay({ tur: 'tip', id: secili.hizmetTipiId })}
                        />}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                    {[
                      { l: 'Kapasite', v: secili.kapasite, a: 'aynı saatte' },
                      { l: 'Süre', v: secili.varsayilanSureDk + ' dk', a: 'varsayılan' },
                      { l: 'Saatler', v: `${secili.acilisSaati}-${secili.kapanisSaati}`, a: 'çalışma' },
                      { l: 'KDV', v: secili.kdvOrani != null ? `%${secili.kdvOrani}` : 'Otel geneli', a: 'oran' },
                    ].map(k => (
                      <div key={k.l} className="bg-slate-50 rounded-2xl px-3 py-3">
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">{k.l}</p>
                        <p className="text-sm font-black text-slate-700 mt-1">{k.v}</p>
                        <p className="text-[9px] text-slate-400">{k.a}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Paketler */}
                <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Paketler</span>
                    <Buton renk="violet" className="!px-5 !py-2.5" onClick={() => setPaketForm({ ...BOS_PAKET })}>
                      + Paket Ekle
                    </Buton>
                  </div>

                  {(secili.paketler || []).length === 0 ? (
                    <EmptyState icon="📦" text="Bu hizmete paket eklenmemiş." />
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {secili.paketler.map(p => (
                        <div key={p.hizmetPaketiId} className="px-6 py-4 flex items-center gap-4 hover:bg-slate-50/60">
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-slate-700">{p.ad}</p>
                            <p className="text-[11px] text-slate-400">
                              {p.sureDk > 0 ? `${p.sureDk} dk` : `${secili.varsayilanSureDk} dk (varsayılan)`}
                              {p.aciklama ? ` · ${p.aciklama}` : ''}
                              {!p.aktifMi && ' · PASİF'}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-black text-slate-700">{para(p.disFiyat, p.paraBirimi)}</p>
                            <p className="text-[10px] font-bold text-slate-400">
                              dışarıdan{kdvNotu(p.disFiyat, secili.kdvOrani, p.paraBirimi) && ` · ${kdvNotu(p.disFiyat, secili.kdvOrani, p.paraBirimi)}`}
                            </p>
                          </div>
                          <div className="text-right min-w-[110px]">
                            {p.misafirFiyat == null ? (
                              <p className="text-sm font-black text-emerald-600">Ücretsiz</p>
                            ) : (
                              <p className="text-sm font-black text-slate-700">{para(p.misafirFiyat, p.paraBirimi)}</p>
                            )}
                            <p className="text-[10px] font-bold text-slate-400">otel misafiri</p>
                          </div>
                          <ActionButtons
                            onEdit={() => setPaketForm({ ...p })}
                            onDelete={() => setSilOnay({ tur: 'paket', id: p.hizmetPaketiId })}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Hizmet formu */}
      {tipForm && (
        <ModalShell
          icon={tipForm.ikon || '🧖'}
          iconColor="from-violet-600 to-fuchsia-700"
          title={tipForm.hizmetTipiId ? 'Hizmeti Düzenle' : 'Yeni Hizmet'}
          subtitle="Kapasite, çalışma saatleri ve KDV oranı"
          size="lg"
          onClose={() => setTipForm(null)}
          footer={
            <>
              <Buton tur="ikincil" onClick={() => setTipForm(null)}>Vazgeç</Buton>
              <Buton renk="violet" onClick={tipKaydet} disabled={!tipForm.ad?.trim()}>💾 Kaydet</Buton>
            </>
          }>

            <div>
              <label className={LBL}>Hizmet Adı *</label>
              <input className={INP} value={tipForm.ad} onChange={e => setTipForm({ ...tipForm, ad: e.target.value })} placeholder="Örn: Çamur Banyosu" />
            </div>

            {/* İkon serbest metin olarak istenmiyor; listeden seçiliyor.
                Yalnızca listede görsel ayırt etmeye yarar. */}
            <div>
              <label className={LBL}>Listede Görünecek Simge</label>
              <div className="flex flex-wrap gap-2">
                {IKONLAR.map(i => (
                  <button key={i} type="button" onClick={() => setTipForm({ ...tipForm, ikon: i })}
                    className={`w-11 h-11 rounded-2xl border text-xl font-emoji transition ${
                      (tipForm.ikon || '🧖') === i
                        ? 'border-violet-400 bg-violet-50 ring-2 ring-violet-100'
                        : 'border-slate-200 hover:border-slate-300'}`}>
                    {i}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className={LBL}>Açıklama</label>
              <input className={INP} value={tipForm.aciklama || ''} onChange={e => setTipForm({ ...tipForm, aciklama: e.target.value })} />
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className={LBL}>Kapasite *</label>
                <input type="number" min="1" className={INP + ' text-center'} value={tipForm.kapasite}
                  onChange={e => setTipForm({ ...tipForm, kapasite: parseInt(e.target.value) || 1 })} />
                <p className="text-[10px] text-slate-400 mt-1">Aynı saatte kaç randevu</p>
              </div>
              <div>
                <label className={LBL}>Süre (dk)</label>
                <input type="number" min="5" step="5" className={INP + ' text-center'} value={tipForm.varsayilanSureDk}
                  onChange={e => setTipForm({ ...tipForm, varsayilanSureDk: parseInt(e.target.value) || 60 })} />
              </div>
              <div>
                <label className={LBL}>Açılış</label>
                <input type="time" className={INP + ' text-center'} value={tipForm.acilisSaati}
                  onChange={e => setTipForm({ ...tipForm, acilisSaati: e.target.value })} />
              </div>
              <div>
                <label className={LBL}>Kapanış</label>
                <input type="time" className={INP + ' text-center'} value={tipForm.kapanisSaati}
                  onChange={e => setTipForm({ ...tipForm, kapanisSaati: e.target.value })} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 items-end">
              <div>
                <label className={LBL}>KDV Oranı (%)</label>
                <input type="number" min="0" max="100" className={INP + ' text-center'} placeholder="Otel geneli oran"
                  value={tipForm.kdvOrani ?? ''}
                  onChange={e => setTipForm({ ...tipForm, kdvOrani: e.target.value === '' ? null : (parseFloat(e.target.value) || 0) })} />
                <p className="text-[10px] text-slate-400 mt-1">Boş = otel geneli oran</p>
              </div>
              <label className="flex items-center gap-3 pb-3 cursor-pointer">
                <input type="checkbox" className="w-5 h-5 rounded text-violet-600" checked={tipForm.aktifMi}
                  onChange={e => setTipForm({ ...tipForm, aktifMi: e.target.checked })} />
                <span className="text-sm font-bold text-slate-700">Aktif</span>
              </label>
            </div>
        </ModalShell>
      )}

      {/* Paket formu */}
      {paketForm && (
        <ModalShell
          icon="📦"
          iconColor="from-violet-600 to-fuchsia-700"
          title={paketForm.hizmetPaketiId ? 'Paketi Düzenle' : 'Yeni Paket'}
          subtitle={secili ? `${secili.ad} · süre ve fiyatlar` : 'Süre ve fiyatlar'}
          size="md"
          onClose={() => setPaketForm(null)}
          footer={
            <>
              <Buton tur="ikincil" onClick={() => setPaketForm(null)}>Vazgeç</Buton>
              <Buton renk="violet" onClick={paketKaydet} disabled={!paketForm.ad?.trim()}>💾 Kaydet</Buton>
            </>
          }>

            <div>
              <label className={LBL}>Paket Adı *</label>
              <input className={INP} value={paketForm.ad} onChange={e => setPaketForm({ ...paketForm, ad: e.target.value })} placeholder="60dk Klasik Masaj" />
            </div>
            <div>
              <label className={LBL}>Açıklama</label>
              <input className={INP} value={paketForm.aciklama || ''} onChange={e => setPaketForm({ ...paketForm, aciklama: e.target.value })} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={LBL}>Süre (dk)</label>
                <input type="number" min="0" step="5" className={INP + ' text-center'} value={paketForm.sureDk}
                  onChange={e => setPaketForm({ ...paketForm, sureDk: parseInt(e.target.value) || 0 })} />
                <p className="text-[10px] text-slate-400 mt-1">0 = hizmetin varsayılanı</p>
              </div>
              <div>
                <label className={LBL}>Para Birimi</label>
                <CustomSelect value={paketForm.paraBirimi} options={paraBirimiSecenekleri}
                  onChange={v => setPaketForm({ ...paketForm, paraBirimi: v })} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={LBL}>Dışarıdan Fiyat *</label>
                <input type="number" min="0" step="0.01" className={INP + ' text-right'} value={paketForm.disFiyat}
                  onChange={e => setPaketForm({ ...paketForm, disFiyat: parseFloat(e.target.value) || 0 })} />
                {tlKarsiligi(paketForm.disFiyat, paketForm.paraBirimi) !== null && (
                  <p className="text-[10px] font-black text-violet-600 bg-violet-50 px-3 py-2 rounded-xl mt-2 inline-flex uppercase tracking-widest">
                    ≈ {tlKarsiligi(paketForm.disFiyat, paketForm.paraBirimi).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                    <span className="opacity-60 ml-1.5">(1 {paketForm.paraBirimi} = {dovizKurlari[paketForm.paraBirimi]} ₺)</span>
                  </p>
                )}
              </div>
              <div>
                <label className={LBL}>Otel Misafiri Fiyatı</label>
                <input type="number" min="0" step="0.01" className={INP + ' text-right'} placeholder="Boş = ücretsiz"
                  value={paketForm.misafirFiyat ?? ''}
                  onChange={e => setPaketForm({ ...paketForm, misafirFiyat: e.target.value === '' ? null : (parseFloat(e.target.value) || 0) })} />
                <p className="text-[10px] text-slate-400 mt-1">Boş bırakılırsa misafire ücretsiz</p>
              </div>
            </div>

            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" className="w-5 h-5 rounded text-violet-600" checked={paketForm.aktifMi}
                onChange={e => setPaketForm({ ...paketForm, aktifMi: e.target.checked })} />
              <span className="text-sm font-bold text-slate-700">Aktif</span>
            </label>
        </ModalShell>
      )}

      <OnayPopup
        acik={!!silOnay}
        baslik={silOnay?.tur === 'tip' ? 'Hizmeti Sil' : 'Paketi Sil'}
        mesaj={silOnay?.tur === 'tip'
          ? 'Bu hizmet ve tüm paketleri silinecek. Randevu kaydı varsa silinemez.'
          : 'Bu paket silinecek. Randevu kaydı varsa silinemez.'}
        onEvet={sil}
        onHayir={() => setSilOnay(null)}
      />

      <UsageInfo
        color="violet"
        description="Hamam, sauna, tuz odası, şok duş, fitness ve masaj gibi randevulu hizmetler buradan tanımlanır. Her hizmetin kendi paketleri ve fiyatları olur."
        scenarios={[
          { title: 'Kapasite ne demek?', content: 'Aynı saat aralığında kabul edilecek en fazla randevu sayısıdır. Masajda çalışan sayısı, saunada aynı anda alınabilecek kişi/oda sayısı gibi düşünün.' },
          { title: 'İki fiyat neden var?', content: 'Dışarıdan gelen müşteri her zaman ücretlidir. Otel misafiri için ayrı fiyat girebilir ya da boş bırakarak ücretsiz sunabilirsiniz.' },
          { title: 'Ödeme nasıl işler?', content: 'Dışarıdan gelen peşin öder. Otel misafiri isterse oda hesabına yazdırır; sistem otomatik adisyon kaydı açar ve tutar check-out hesabına eklenir.' },
          { title: 'Vergi', content: 'Hizmetin KDV oranı burada tanımlanır, satış anında randevuya kopyalanır. Otel misafirine verilen hizmet konaklama vergisi kapsamındadır; dışarıdan gelene verilen değildir.' },
        ]}
      />
    </div>
  );
}
